import React, { useState, useEffect } from 'react';
import { FileText, Wallet, CreditCard, User, Lock, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import API_BASE_URL from '../Config';
import SocioSearch from './SocioSearch';
import SocioDatos from './socio-components/SocioDatos';
import SocioObligaciones from './socio-components/SocioObligaciones';
import SocioCuentas from './socio-components/SocioCuentas';
import SocioCreditos from './socio-components/SocioCreditos';
import SocioDocumentos from './socio-components/SocioDocumentos';

export default function SocioMain() {
    const { token } = useAuth();

    // State
    const [selectedSocio, setSelectedSocio] = useState(null);
    const [activeTab, setActiveTab] = useState('datos');
    const [loadingDetails, setLoadingDetails] = useState(false);

    // Data State for Tabs
    const [creditos, setCreditos] = useState([]);
    const [loadingCreditos, setLoadingCreditos] = useState(false);

    const [documentos, setDocumentos] = useState([]);
    const [loadingDocs, setLoadingDocs] = useState(false);

    const [obligaciones, setObligaciones] = useState([]);
    const [loadingObligaciones, setLoadingObligaciones] = useState(false);

    // Helper functions
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    const formatCurrency = (amount, currencyCode = 'PYG') => {
        return new Intl.NumberFormat('es-PY', {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(amount || 0);
    };

    // Handlers
    const handleSocioSelect = async (socioSummary) => {
        // When a socio is selected from search
        setLoadingDetails(true);
        try {
            // Fetch full details
            const response = await fetch(`${API_BASE_URL}/socio/${socioSummary.id}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (response.ok) {
                const fullSocio = await response.json();
                setSelectedSocio(fullSocio);
                setActiveTab('datos'); // Reset to first tab
            } else {
                console.error("Error fetching full socio details");
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingDetails(false);
        }
    };

    // Data Fetching Effects based on Active Tab
    useEffect(() => {
        if (!selectedSocio) return;

        const id = selectedSocio.id;

        if (activeTab === 'creditos') {
            const fetchCreditos = async () => {
                setLoadingCreditos(true);
                try {
                    const response = await fetch(`${API_BASE_URL}/creditos/socio/${id}/resumen`, {
                        headers: token ? { Authorization: `Bearer ${token}` } : {},
                    });
                    if (response.ok) {
                        const data = await response.json();
                        setCreditos(data || []);
                    } else if (response.status === 404) {
                        setCreditos([]);
                    }
                } catch (error) {
                    console.error(error);
                } finally {
                    setLoadingCreditos(false);
                }
            };
            fetchCreditos();
        }

        if (activeTab === 'documentos') {
            const fetchDocumentos = async () => {
                setLoadingDocs(true);
                try {
                    const response = await fetch(`${API_BASE_URL}/socio/${id}/documentos`, {
                        headers: token ? { Authorization: `Bearer ${token}` } : {},
                    });
                    if (response.ok) {
                        const data = await response.json();
                        setDocumentos(data);
                    }
                } catch (error) { console.error(error); }
                finally { setLoadingDocs(false); }
            };
            fetchDocumentos();
        }

        if (activeTab === 'obligaciones') {
            const fetchObligaciones = async () => {
                setLoadingObligaciones(true);
                try {
                    // Use /pendientes endpoint for Consolidated Position
                    const response = await fetch(`${API_BASE_URL}/obligaciones/pendientes?socioId=${id}`, {
                        headers: token ? { Authorization: `Bearer ${token}` } : {},
                    });
                    if (response.ok) {
                        const data = await response.json();
                        // The endpoint returns { socioId, ..., obligaciones: [...] }
                        setObligaciones(data.obligaciones || []);
                    } else if (response.status === 404) {
                        setObligaciones([]);
                    }
                } catch (error) { console.error(error); }
                finally { setLoadingObligaciones(false); }
            };
            fetchObligaciones();
        }

    }, [selectedSocio, activeTab, token]);


    const tabs = [
        { id: 'datos', label: 'Datos del Socio', icon: <User className="w-4 h-4" />, disabled: false },
        { id: 'obligaciones', label: 'Obligaciones', icon: <FileText className="w-4 h-4" />, disabled: !selectedSocio },
        { id: 'cuentas', label: 'Resumen de Cuentas', icon: <Wallet className="w-4 h-4" />, disabled: !selectedSocio },
        { id: 'creditos', label: 'Historial de Créditos', icon: <CreditCard className="w-4 h-4" />, disabled: !selectedSocio },
        { id: 'documentos', label: 'Documentos', icon: <FileText className="w-4 h-4" />, disabled: !selectedSocio },
    ];

    return (
        <div className="pb-20">
            {/* 1. TOP SEARCH AREA */}
            <div className="bg-white border-b border-gray-100 sticky top-0 z-20">
                {/* Compact Search Wrapper */}
                <div className="w-full">
                    <div className={`${selectedSocio ? 'py-3' : 'py-10'} transition-all duration-300`}>
                        <SocioSearch onSocioSelect={handleSocioSelect} compact={!!selectedSocio} />
                    </div>
                </div>

                {/* 2. TAB NAVIGATION BAR */}
                <div className="w-full">
                    <div className="flex space-x-10 overflow-x-auto scrollbar-hide px-4">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => !tab.disabled && setActiveTab(tab.id)}
                                disabled={tab.disabled}
                                className={`
                                    py-5 px-1 inline-flex items-center gap-2 border-b-2 font-black text-xs uppercase tracking-widest whitespace-nowrap transition-all duration-200
                                    ${activeTab === tab.id
                                        ? 'border-uni-green text-uni-green'
                                        : tab.disabled
                                            ? 'border-transparent text-slate-200 cursor-not-allowed'
                                            : 'border-transparent text-slate-400 hover:text-uni-green hover:border-uni-green/20'
                                    }
                                `}
                            >
                                {tab.disabled ? <Lock className="w-3.5 h-3.5 opacity-30" /> : React.cloneElement(tab.icon, { className: "w-4 h-4" })}
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* 3. MAIN CONTENT AREA */}
            <div className="py-12 max-w-7xl mx-auto px-4 md:px-8">
                {loadingDetails && (
                    <div className="flex justify-center py-20">
                        <Loader2 className="animate-spin h-10 w-10 text-uni-green" />
                    </div>
                )}

                {!loadingDetails && selectedSocio && (
                    <div className="enterprise-card min-h-[500px] p-6 md:p-8 animate-premium-in">
                        {activeTab === 'datos' && <SocioDatos socio={selectedSocio} formatDate={formatDate} />}

                        {activeTab === 'obligaciones' && (
                            <SocioObligaciones
                                obligaciones={obligaciones}
                                loading={loadingObligaciones}
                                formatDate={formatDate}
                                formatCurrency={formatCurrency}
                            />
                        )}

                        {activeTab === 'cuentas' && (
                            <SocioCuentas
                                cuentas={selectedSocio.cuentas}
                                formatCurrency={formatCurrency}
                            />
                        )}

                        {activeTab === 'creditos' && (
                            <SocioCreditos
                                creditos={creditos}
                                loading={loadingCreditos}
                                socioId={selectedSocio.id}
                                formatDate={formatDate}
                                formatCurrency={formatCurrency}
                            />
                        )}

                        {activeTab === 'documentos' && (
                            <SocioDocumentos
                                documentos={documentos}
                                loading={loadingDocs}
                                formatDate={formatDate}
                            />
                        )}
                    </div>
                )}

                {!selectedSocio && !loadingDetails && (
                    <div className="text-center py-24 bg-white border border-dashed border-slate-200 rounded-enterprise">
                        <div className="bg-slate-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 border border-slate-100">
                            <User className="w-8 h-8 text-slate-300" />
                        </div>
                        <h3 className="text-lg font-bold text-slate-900">Consulta de Socios</h3>
                        <p className="text-enterprise-muted text-sm max-w-xs mx-auto mt-1">Utilice el motor de búsqueda superior para localizar un socio y gestionar su legajo digital.</p>
                    </div>
                )}
            </div>
        </div>
    );
}
